import { Location, NgStyle } from '@angular/common';
import {
  afterEveryRender,
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import { CadsIconViewBitComponent } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.component';
import type { CadsIconViewBitConfig } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.config';
import { CadsAppButtonComponent } from '../cads-app-button/cads-app-button.component';
import type { CadsAppButtonConfig } from '../cads-app-button/cads-app-button.config';
import { CadsHeadingDetailBitComponent } from '../../bits/cads-heading-detail-bit/cads-heading-detail-bit.component';
import type { CadsHeadingDetailBitConfig } from '../../bits/cads-heading-detail-bit/cads-heading-detail-bit.config';

import type {
  CadsMainTemplateCtaConfig,
  CadsMainTemplateConfig,
} from './cads-main-template.config';
import {
  CADS_MAIN_TEMPLATE_ANALYTICS,
  cleanCadsMainTemplateFbEventName,
} from './cads-main-template-analytics';
import { CADS_MAIN_TEMPLATE_MODAL_CONTEXT } from './cads-main-template-modal.context';

/**
 * Desktop-oriented **article** shell: centered readable column, optional projected **header**, optional built-in **back**,
 * **hero**, {@link CadsHeadingDetailBitConfig}, **body** (projected into a **scrollable middle** between pinned header and
 * footer), and **footer** with
 * either projected CTAs or **config-driven** {@link CadsAppButtonComponent} pairs (**secondary** first, **primary** last,
 * end-aligned; built-in **back** is icon + text only, not an app button). Optional {@link CadsMainTemplateConfig.footerConfirmation}
 * adds a centered checkbox in the pinned footer that gates the **primary** CTA until checked. Header, body, and footer visibility default from **projected** `[cads-main-header]` / `[cads-main-body]` /
 * `[cads-main-footer]` (after render); set {@link CadsMainTemplateConfig.showHeader} / `showBody` / `showFooter` to **true**
 * or **false** to force show or hide. Use {@link CadsMainTemplateComponent.secondaryTapped}, {@link CadsMainTemplateComponent.primaryTapped},
 * and {@link CadsMainTemplateComponent.backTapped} for custom navigation, services, and error handling; optional
 * `navigate` on CTAs runs synchronous Router navigation when Router is available unless {@link CadsMainTemplateCtaConfig.skipNavigation} is **true**.
 * Optional {@link CadsMainTemplateConfig.analytics} with `CADS_MAIN_TEMPLATE_ANALYTICS` logs **shown** / tap events in
 * `FbDirective` naming style for built-in chrome.
 */
@Component({
    selector: 'cads-main-template',
    imports: [CadsAppButtonComponent, CadsHeadingDetailBitComponent, CadsIconViewBitComponent, NgStyle],
    templateUrl: './cads-main-template.component.html',
    styleUrl: './cads-main-template.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CadsMainTemplateComponent {
  readonly config = input.required<CadsMainTemplateConfig>();

  /** Fires after optional `Location.back()` when back {@link CadsMainTemplateConfig.back} behavior is **location**. */
  readonly backTapped = output<void>();
  /** Fires after optional Router navigation for {@link CadsMainTemplateConfig.secondaryCta}. */
  readonly secondaryTapped = output<void>();
  /** Fires after optional Router navigation for {@link CadsMainTemplateConfig.primaryCta}. */
  readonly primaryTapped = output<void>();

  private readonly _location = inject(Location, { optional: true });
  private readonly _router = inject(Router, { optional: true });
  private readonly _host = inject(ElementRef<HTMLElement>);
  private readonly _injector = inject(Injector);
  private readonly _mainScroll = viewChild.required<ElementRef<HTMLElement>>('mainScroll');
  private readonly _analyticsSink = inject(CADS_MAIN_TEMPLATE_ANALYTICS, { optional: true });
  private readonly _inModal = inject(CADS_MAIN_TEMPLATE_MODAL_CONTEXT, { optional: true }) === true;

  private readonly _hasProjectedHeader = signal(false);
  private readonly _hasProjectedHero = signal(false);
  private readonly _hasProjectedBody = signal(false);
  private readonly _hasProjectedFooter = signal(false);
  private readonly _analyticsShownLogged = signal(false);

  /** Checked state for optional {@link CadsMainTemplateConfig.footerConfirmation}; synced when that config slice changes. */
  private readonly _footerConfirmationChecked = signal(false);
  /** Exposed for the template (read-only view of {@link _footerConfirmationChecked}). */
  readonly footerConfirmationChecked = this._footerConfirmationChecked.asReadonly();
  private _footerConfirmationConfigKey = '';

  private readonly _syncFooterConfirmationFromConfig = effect(
    () => {
      const fc = this.config().footerConfirmation;
      const key = `${fc?.label ?? ''}\0${fc?.initiallyChecked ?? false}`;
      if (key !== this._footerConfirmationConfigKey) {
        this._footerConfirmationConfigKey = key;
        this._footerConfirmationChecked.set(fc?.initiallyChecked ?? false);
      }
    },
    { injector: this._injector, allowSignalWrites: true },
  );

  /** Fires once when `config().analytics` + sink are available (after inputs are bound). */
  private readonly _analyticsShownEffect = effect(
    () => {
      if (this._analyticsShownLogged()) {
        return;
      }
      const sink = this._analyticsSink;
      const attrs = this.config().analytics;
      if (!sink || !attrs?.event_name?.trim()) {
        return;
      }
      const base = cleanCadsMainTemplateFbEventName(attrs.event_name);
      sink.logEvent(`${base} shown`, this._analyticsCustomParams(attrs));
      this._analyticsShownLogged.set(true);
    },
    { injector: this._injector, allowSignalWrites: true },
  );

  constructor() {
    afterNextRender(
      () => {
        this._mainScroll().nativeElement.scrollTop = 0;
      },
      { injector: this._injector },
    );

    afterEveryRender(
      () => {
        const root = this._host.nativeElement;
        const h = root.querySelector('[cads-main-header]') != null;
        const hero = root.querySelector('[cads-main-hero]') != null;
        const b = root.querySelector('[cads-main-body]') != null;
        const f = root.querySelector('[cads-main-footer]') != null;
        if (h !== this._hasProjectedHeader()) {
          this._hasProjectedHeader.set(h);
        }
        if (hero !== this._hasProjectedHero()) {
          this._hasProjectedHero.set(hero);
        }
        if (b !== this._hasProjectedBody()) {
          this._hasProjectedBody.set(b);
        }
        if (f !== this._hasProjectedFooter()) {
          this._hasProjectedFooter.set(f);
        }
      },
      { injector: this._injector },
    );
  }

  private readonly _density = computed(() => this.config().density ?? 'full');

  /**
   * `[cads-main-header]` slot visibility: **false** hides; **true** shows (may be empty); **undefined** uses projection
   * detection after render.
   */
  readonly effectiveShowHeaderSlot = computed(() => {
    const v = this.config().showHeader;
    if (v === false) {
      return false;
    }
    if (v === true) {
      return true;
    }
    return this._hasProjectedHeader();
  });

  /** `[cads-main-hero]` slot visibility from projection detection after render. */
  readonly showHeroRegion = computed(() => this._hasProjectedHero());

  /** Intro band (hero + intro heading bit) when either child region has content. */
  readonly showIntroRegion = computed(
    () => this.showHeroRegion() || this.headingForTemplate() != null,
  );

  /**
   * Body region visibility: **false** hides; **true** shows; **undefined** uses projection detection after render.
   */
  readonly effectiveShowBody = computed(() => {
    const v = this.config().showBody;
    if (v === false) {
      return false;
    }
    if (v === true) {
      return true;
    }
    return this._hasProjectedBody();
  });

  /**
   * Footer region visibility: **false** hides; **true** shows; **undefined** uses projection detection or built-in CTAs.
   */
  readonly effectiveShowFooter = computed(() => {
    const v = this.config().showFooter;
    if (v === false) {
      return false;
    }
    if (v === true) {
      return true;
    }
    return this._hasProjectedFooter() || this.useConfigFooter();
  });

  readonly showBuiltInBack = computed(() => {
    if (this._inModal) {
      return false;
    }
    const b = this.config().back;
    if (b == null) {
      return false;
    }
    return b.visible !== false;
  });

  /** Centered page title in the sticky header (from {@link CadsMainTemplateConfig.heading}.title). */
  readonly headerTitle = computed(() => this.config().heading?.title?.trim() || null);

  /** Renders the `<header>` strip when back, header slot, or {@link headerTitle} is shown. */
  readonly showHeaderRegion = computed(
    () => this.showBuiltInBack() || this.effectiveShowHeaderSlot() || this.headerTitle() != null
  );

  /** When set, built-in footer buttons are used and `[cads-main-footer]` projection is skipped. */
  readonly useConfigFooter = computed(() => {
    const c = this.config();
    return c.secondaryCta != null || c.primaryCta != null;
  });

  readonly secondaryCta = computed((): CadsMainTemplateCtaConfig | null => this.config().secondaryCta ?? null);
  readonly primaryCta = computed((): CadsMainTemplateCtaConfig | null => this.config().primaryCta ?? null);

  /**
   * Optional footer checkbox: non-empty {@link CadsMainTemplateConfig.footerConfirmation} label and a
   * {@link primaryCta} (built-in footer only).
   */
  readonly showFooterConfirmation = computed(() => {
    const label = this.config().footerConfirmation?.label?.trim();
    return !!label && this.primaryCta() != null;
  });

  readonly footerConfirmationLabel = computed(() => this.config().footerConfirmation?.label?.trim() ?? '');

  /** Visible back label (host copy; default “Back”). */
  readonly backLabel = computed(() => {
    const raw = this.config().back?.button?.label?.trim();
    return raw || 'Back';
  });

  readonly backDisabled = computed(
    () => this._inModal || (this.config().back?.button?.disabled ?? false),
  );

  /** Sprite id before the label; default `chevron_left`. */
  readonly backIconConfig = computed((): CadsIconViewBitConfig => {
    const raw = this.config().back?.button?.leadingIcon?.trim();
    const icon = raw || 'chevron_left';
    return {
      icon,
      shape: 'icon',
      size: 'medium',
      collapsed: true,
      iconSizePx: 20,
      iconColor: 'currentColor',
      color: 'var(--cads-color-mid-gray)',
    };
  });

  readonly secondaryButtonConfig = computed((): CadsAppButtonConfig => {
    const c = this.secondaryCta();
    if (!c) {
      return { label: '', style: 'secondary', hug: true, disabled: true };
    }
    const b = c.button;
    return {
      ...b,
      style: b.style ?? 'secondary',
      hug: true,
      disabled: (b.disabled ?? false) || (b.processing ?? false),
      processing: b.processing ?? false,
    };
  });

  readonly primaryButtonConfig = computed((): CadsAppButtonConfig => {
    const c = this.primaryCta();
    if (!c) {
      return { label: '', style: 'primary', hug: false, disabled: true };
    }
    const b = c.button;
    const gated = this.showFooterConfirmation() && !this._footerConfirmationChecked();
    return {
      ...b,
      style: b.style ?? 'primary',
      hug: b.hug ?? false,
      disabled: (b.disabled ?? false) || gated || (b.processing ?? false),
      processing: b.processing ?? false,
    };
  });

  /** True when the middle block (hero + heading) should be vertically centered (compact, no body). */
  readonly compactCentered = computed(() => this._density() === 'compact' && !this.effectiveShowBody());

  /** Shell + CSS variables for the centered column (desktop-style). */
  readonly articleStyles = computed((): Record<string, string> => {
    const c = this.config();
    const styles: Record<string, string> = {
      'max-width': c.maxWidth ?? '100%',
      '--cads-main-template-gutter': c.edgeToEdge === true ? '0' : 'var(--cads-space-md)',
      '--cads-main-template-inner-max-width':
        c.edgeToEdge === true ? 'none' : (c.innerMaxWidth ?? 'min(100%, 40rem)'),
    };
    const minPx = c.minHeightPx;
    if (minPx != null && Number.isFinite(minPx) && minPx > 0) {
      styles['min-height'] = `${minPx}px`;
    }
    return styles;
  });

  /**
   * Intro heading bit: only when eyebrow, subtitle, or badge are set. Plain titles render in the sticky header via
   * {@link headerTitle}; when those extras exist, the title row in the bit is hidden if the header already shows it.
   */
  readonly headingForTemplate = computed((): CadsHeadingDetailBitConfig | null => {
    const h = this.config().heading;
    if (!h || !this._headingHasIntroExtras(h)) {
      return null;
    }
    const defaultSize: NonNullable<CadsHeadingDetailBitConfig['size']> =
      this._density() === 'compact' ? 'small' : 'medium';
    return { ...h, size: h.size ?? defaultSize };
  });

  private _headingHasIntroExtras(h: CadsHeadingDetailBitConfig): boolean {
    return !!(h.eyebrow?.trim() || h.subtitle?.trim() || h.badgeLabel?.trim());
  }

  protected _onBackTap(): void {
    if (this.backDisabled()) {
      return;
    }
    this._logAnalyticsInteraction('dismissed');
    const b = this.config().back;
    const mode = b?.behavior ?? 'location';
    if (mode === 'location' && this._location) {
      this._location.back();
    }
    this.backTapped.emit();
  }

  protected _onSecondaryTap(): void {
    this._logAnalyticsInteraction('secondary tapped');
    const c = this.secondaryCta();
    if (c && c.skipNavigation !== true) {
      this._navigateFromCta(c.navigate);
    }
    this.secondaryTapped.emit();
  }

  protected _onPrimaryTap(): void {
    if (this.showFooterConfirmation() && !this._footerConfirmationChecked()) {
      return;
    }
    this._logAnalyticsInteraction('tapped');
    const c = this.primaryCta();
    if (c && c.skipNavigation !== true) {
      this._navigateFromCta(c.navigate);
    }
    this.primaryTapped.emit();
  }

  protected _onFooterConfirmationChange(ev: Event): void {
    const el = ev.target as HTMLInputElement | null;
    if (!el) {
      return;
    }
    this._footerConfirmationChecked.set(el.checked);
  }

  private _navigateFromCta(navigate: string | readonly unknown[] | undefined): void {
    const r = this._router;
    if (!r || navigate == null) {
      return;
    }
    if (typeof navigate === 'string') {
      void r.navigateByUrl(navigate);
      return;
    }
    if (navigate.length > 0) {
      void r.navigate([...navigate]);
    }
  }

  private _logAnalyticsInteraction(suffix: 'tapped' | 'secondary tapped' | 'dismissed'): void {
    const sink = this._analyticsSink;
    const attrs = this.config().analytics;
    if (!sink || !attrs?.event_name?.trim()) {
      return;
    }
    const base = cleanCadsMainTemplateFbEventName(attrs.event_name);
    sink.logEvent(`${base} ${suffix}`, this._analyticsCustomParams(attrs));
  }

  private _analyticsCustomParams(attrs: NonNullable<CadsMainTemplateConfig['analytics']>): { [key: string]: string } | undefined {
    if (attrs.custom == null) {
      return undefined;
    }
    return { ...attrs.custom };
  }
}
